import React from 'react';
import { Plus, Banknote, CreditCard, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { CurrencyLabel } from '../CurrencyDisplay';
import LoyaltyMemberCard from './LoyaltyMemberCard';

const AddRevenueDialog = ({
  open,
  onOpenChange,
  language,
  formData,
  setFormData,
  calculatedDay,
  handleDateChange,
  handleTimeChange,
  timeWarning,
  loyaltyMember,
  setLoyaltyMember,
  lookingUpLoyalty,
  lookupLoyaltyMember,
  redeemPoints,
  setRedeemPoints,
  onSubmit,
  isSubmitting = false,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button data-testid="add-revenue-btn" className="bg-primary text-primary-foreground">
          <Plus className="w-4 h-4 me-2" />
          إضافة إيراد
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-white/10 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>إضافة إيراد جديد</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">{language === 'ar' ? 'التاريخ' : 'Date'}</label>
              <Input
                type="date"
                value={formData.receipt_date}
                onChange={(e) => handleDateChange(e.target.value)}
                className="bg-secondary/50 border-white/10"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">{language === 'ar' ? 'اليوم' : 'Day'}</label>
              <Input
                type="text"
                value={calculatedDay}
                readOnly
                className="bg-secondary/30 border-white/10 text-muted-foreground cursor-not-allowed"
                placeholder={language === 'ar' ? 'يُحسب تلقائياً' : 'Auto-calculated'}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">{language === 'ar' ? 'الوقت' : 'Time'}</label>
              <Input
                type="time"
                value={formData.receipt_time}
                onChange={(e) => handleTimeChange(e.target.value)}
                className={`bg-secondary/50 border-white/10 ${timeWarning ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                required
              />
            </div>
          </div>

          {timeWarning && (
            <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              <AlertTriangle className="w-5 h-5 flex-shrink-0" />
              <span>{timeWarning}</span>
            </div>
          )}

          <LoyaltyMemberCard
            language={language}
            membershipNumber={formData.membership_number}
            onMembershipChange={(v) => {
              setFormData({ ...formData, membership_number: v });
              if (loyaltyMember) setLoyaltyMember(null);
              setRedeemPoints('');
            }}
            onLookup={lookupLoyaltyMember}
            loyaltyMember={loyaltyMember}
            loading={lookingUpLoyalty}
            redeemPoints={redeemPoints}
            onRedeemChange={setRedeemPoints}
            billAmount={formData.amount}
          />

          <div>
            <label className="block text-sm font-medium mb-2">
              <CurrencyLabel label="المبلغ" />
            </label>
            <Input
              data-testid="revenue-amount-input"
              type="number"
              step="0.01"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              required
              placeholder="0.00"
              className="bg-secondary/50 border-white/10"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">عدد الزبائن</label>
            <Input
              data-testid="revenue-clients-input"
              type="number"
              min="1"
              value={formData.client_count}
              onChange={(e) => setFormData({ ...formData, client_count: e.target.value })}
              required
              className="bg-secondary/50 border-white/10"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">طريقة الدفع</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer p-3 rounded-lg border border-white/10 flex-1 hover:bg-secondary/30 transition-colors">
                <input
                  type="radio"
                  name="payment_method"
                  value="cash"
                  checked={formData.payment_method === 'cash'}
                  onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                  className="w-4 h-4 accent-primary"
                />
                <Banknote className="w-5 h-5 text-green-500" />
                <span>كاش</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer p-3 rounded-lg border border-white/10 flex-1 hover:bg-secondary/30 transition-colors">
                <input
                  type="radio"
                  name="payment_method"
                  value="machine"
                  checked={formData.payment_method === 'machine'}
                  onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                  className="w-4 h-4 accent-primary"
                />
                <CreditCard className="w-5 h-5 text-blue-500" />
                <span>مكينة</span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              {language === 'ar' ? 'ملاحظات (اختياري)' : 'Notes (optional)'}
            </label>
            <Input
              data-testid="revenue-desc-input"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder={language === 'ar' ? 'مثال: قص شعر + لحية' : 'Example: Haircut + Beard'}
              className="bg-secondary/50 border-white/10"
            />
          </div>

          <Button
            data-testid="submit-revenue-btn"
            type="submit"
            className="w-full bg-primary text-primary-foreground"
            disabled={!!timeWarning || isSubmitting}
          >
            {isSubmitting
              ? language === 'ar' ? 'جاري الحفظ...' : 'Saving...'
              : language === 'ar' ? 'حفظ الإيراد' : 'Save Revenue'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddRevenueDialog;
